import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Container,
    Typography,
    Grid,
    Paper,
    Button,
    Divider,
} from '@mui/material';
import { Groups, School, Favorite } from '@mui/icons-material';

function About() {
    const { t } = useTranslation();

    // Safely extract the Mission Sections array
    const missionRaw = t('AboutPage.MissionSections', { returnObjects: true });
    const aboutSections = Array.isArray(missionRaw) ? missionRaw : [];

    // Safely extract the Values array and map the MUI icons to them
    const valuesRaw = t('AboutPage.ValuesList', { returnObjects: true });
    const valuesList = Array.isArray(valuesRaw) ? valuesRaw : [];
    const valueIcons = [
        <Groups sx={{ fontSize: 48, color: 'primary.main' }} />,
        <School sx={{ fontSize: 48, color: 'primary.main' }} />,
        <Favorite sx={{ fontSize: 48, color: 'primary.main' }} />
    ];
    const values = valuesList.map((val, index) => ({ ...val, icon: valueIcons[index] }));

    const partners = [
        { name: 'Lucerne University', logo: 'lucerne-university.png' },
        { name: 'Fenwick Speech Therapy', logo: 'fenwick-speech-therapy.png' }
    ];

    return (
        <Box>
            {/* Hero Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #7B1FA2 0%, #512DA8 100%)',
                    color: 'white',
                    py: { xs: 6, md: 10 },
                    px: 2,
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <Typography
                                variant="h2"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '2rem', md: '3rem' },
                                    mb: 3,
                                }}
                            >
                                {t('AboutPage.HeroTitle')}
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    opacity: 0.9,
                                    lineHeight: 1.7,
                                    mb: 4,
                                    fontWeight: 400,
                                }}
                            >
                                {t('AboutPage.HeroSubtitle')}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Button
                                    component={Link}
                                    to="/contactus"
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        bgcolor: 'white',
                                        color: 'primary.main',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 2,
                                    }}
                                >
                                    {t('AboutPage.ContactUs')}
                                </Button>
                                <Button
                                    href="https://vividkids.org/support-us"
                                    target="_blank"
                                    variant="outlined"
                                    size="large"
                                    sx={{
                                        borderColor: 'white',
                                        color: 'white',
                                        '&:hover': {
                                            borderColor: 'white',
                                            bgcolor: 'rgba(255,255,255,0.1)',
                                        },
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 2,
                                    }}
                                >
                                    {t('AboutPage.SupportUs')}
                                </Button>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Box
                                component="img"
                                src="child-reading.png"
                                alt="Child reading"
                                sx={{
                                    width: '100%',
                                    maxHeight: 400,
                                    objectFit: 'cover',
                                    borderRadius: 3,
                                    boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                                }}
                            />
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Mission Section */}
            <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
                <Typography
                    variant="h4"
                    align="center"
                    sx={{ fontWeight: 600, mb: 2 }}
                >
                    {t('AboutPage.MissionTitle')}
                </Typography>
                <Divider sx={{ width: 80, mx: 'auto', mb: 6, borderColor: 'primary.main', borderWidth: 2 }} />

                <Grid container spacing={4}>
                    {aboutSections.map((text, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 4,
                                    height: '100%',
                                    bgcolor: 'grey.50',
                                    borderRadius: 3,
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: 3,
                                    },
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{ lineHeight: 1.8, color: 'text.secondary' }}
                                >
                                    {text}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Values Section */}
            <Box sx={{ bgcolor: 'grey.100', py: { xs: 6, md: 10 } }}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h4"
                        align="center"
                        sx={{ fontWeight: 600, mb: 2 }}
                    >
                        {t('AboutPage.ValuesTitle')}
                    </Typography>
                    <Divider sx={{ width: 80, mx: 'auto', mb: 6, borderColor: 'primary.main', borderWidth: 2 }} />

                    <Grid container spacing={4}>
                        {values.map((value, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <Paper
                                    elevation={2}
                                    sx={{
                                        p: 4,
                                        textAlign: 'center',
                                        height: '100%',
                                        borderRadius: 3,
                                    }}
                                >
                                    <Box sx={{ mb: 2 }}>{value.icon}</Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                        {value.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {value.description}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Partners Section */}
            <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
                <Typography
                    variant="h4"
                    align="center"
                    sx={{ fontWeight: 600, mb: 2 }}
                >
                    {t('AboutPage.PartnersTitle')}
                </Typography>
                <Divider sx={{ width: 80, mx: 'auto', mb: 6, borderColor: 'primary.main', borderWidth: 2 }} />

                <Grid container spacing={4} justifyContent="center" alignItems="center">
                    {partners.map((partner, index) => (
                        <Grid item key={index}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 4,
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'grey.200',
                                    transition: 'box-shadow 0.2s',
                                    '&:hover': { boxShadow: 2 },
                                }}
                            >
                                <Box
                                    component="img"
                                    src={partner.logo}
                                    alt={partner.name}
                                    sx={{
                                        height: 80,
                                        width: 'auto',
                                        objectFit: 'contain',
                                        filter: 'grayscale(30%)',
                                        transition: 'filter 0.2s',
                                        '&:hover': { filter: 'grayscale(0%)' },
                                    }}
                                />
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* CTA Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #E8DEF8 0%, #F3E5F5 100%)',
                    py: { xs: 6, md: 10 },
                    px: 2,
                }}
            >
                <Container maxWidth="md">
                    <Paper
                        elevation={3}
                        sx={{
                            p: { xs: 4, md: 6 },
                            textAlign: 'center',
                            borderRadius: 4,
                        }}
                    >
                        <Typography
                            variant="h4"
                            sx={{ fontWeight: 600, mb: 2 }}
                        >
                            {t('AboutPage.CTATitle')}
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 4, lineHeight: 1.8, maxWidth: 700, mx: 'auto' }}
                        >
                            {t('AboutPage.CTADesc')}
                        </Typography>
                        <Button
                            component={Link}
                            to="/contactus"
                            variant="contained"
                            size="large"
                            sx={{
                                px: 5,
                                py: 1.5,
                                borderRadius: 2,
                            }}
                        >
                            {t('AboutPage.GetInTouch')}
                        </Button>
                    </Paper>
                </Container>
            </Box>
        </Box>
    );
}

export default About;
